/**
 * Letters as data: each glyph is its strokes in the order they are written, each stroke a directed
 * polyline in a unit box (x right, y up; 0 is the baseline, 1 the cap height; a glyph is `width`
 * wide). The same data carves a groove, embosses a block, plays a ghost drawing and checks a child's
 * tracing (`followPath`), so the engine never knows what a letter is.
 *
 * A writing model is one `GlyphSet`. The first is the German school print capitals
 * (Druckschrift / Grundschrift as first taught): straight lines top to bottom and left to right,
 * round letters anticlockwise from the top right, a vertical stem before the strokes hanging off
 * it. Other school models are other sets with the same shape.
 */

export type Pt = readonly [number, number]
export type Glyph = {
  char: string
  /** Width of the glyph box in cap heights. */
  width: number
  /** Strokes in writing order; each one runs from its first point to its last. */
  strokes: readonly (readonly Pt[])[]
}
export type GlyphSet = {
  id: string
  name: string
  glyphs: Readonly<Record<string, Glyph>>
}

/**
 * An elliptical arc from angle `a0` to `a1` (degrees, 0 = right, 90 = up; a1 > a0 runs
 * anticlockwise), centre (cx, cy), radii (rx, ry). About one point per 10 degrees.
 */
export function arc(cx: number, cy: number, rx: number, ry: number, a0: number, a1: number): Pt[] {
  const n = Math.max(2, Math.ceil(Math.abs(a1 - a0) / 10))
  const out: Pt[] = []
  for (let i = 0; i <= n; i++) {
    const a = ((a0 + ((a1 - a0) * i) / n) * Math.PI) / 180
    out.push([cx + rx * Math.cos(a), cy + ry * Math.sin(a)])
  }
  return out
}

/** Join pieces into one stroke, dropping a repeated joint point. */
function path(...pieces: (readonly Pt[])[]): Pt[] {
  const out: Pt[] = []
  for (const piece of pieces) {
    for (const p of piece) {
      const last = out[out.length - 1]
      if (last && Math.abs(last[0] - p[0]) < 1e-6 && Math.abs(last[1] - p[1]) < 1e-6) continue
      out.push(p)
    }
  }
  return out
}

const g = (char: string, width: number, ...strokes: (readonly Pt[])[]): Glyph => ({ char, width, strokes })

/** German school print capitals (Druckschrift). */
const GERMAN_CAPITALS: Glyph[] = [
  // A: left leg down from the top, right leg down from the top, then the bar left to right.
  g('A', 0.8, [[0.4, 1], [0.02, 0]], [[0.4, 1], [0.78, 0]], [[0.17, 0.38], [0.63, 0.38]]),
  // B: the stem down, then from the top two bows to the right (clockwise) and back to the stem.
  g('B', 0.66, [[0.04, 1], [0.04, 0]], path([[0.04, 1], [0.36, 1]], arc(0.36, 0.765, 0.24, 0.235, 90, -90), arc(0.38, 0.265, 0.27, 0.265, 90, -90), [[0.04, 0]])),
  // C: anticlockwise from the top right round to the bottom right.
  g('C', 0.76, arc(0.42, 0.5, 0.38, 0.5, 45, 315)),
  // D: the stem down, then one bow from the top round to the bottom.
  g('D', 0.74, [[0.04, 1], [0.04, 0]], path([[0.04, 1], [0.26, 1]], arc(0.26, 0.5, 0.44, 0.5, 90, -90), [[0.04, 0]])),
  // E: the stem down, then top, middle and bottom bars left to right.
  g('E', 0.6, [[0.04, 1], [0.04, 0]], [[0.04, 1], [0.56, 1]], [[0.04, 0.52], [0.48, 0.52]], [[0.04, 0], [0.56, 0]]),
  // F: the stem down, then top and middle bars.
  g('F', 0.58, [[0.04, 1], [0.04, 0]], [[0.04, 1], [0.54, 1]], [[0.04, 0.52], [0.46, 0.52]]),
  // G: the C, then up to the middle and in.
  g('G', 0.8, path(arc(0.42, 0.5, 0.38, 0.5, 45, 360), [[0.8, 0.42], [0.5, 0.42]])),
  // H: left stem, right stem, the bar.
  g('H', 0.72, [[0.04, 1], [0.04, 0]], [[0.68, 1], [0.68, 0]], [[0.04, 0.52], [0.68, 0.52]]),
  // I: one stem.
  g('I', 0.12, [[0.06, 1], [0.06, 0]]),
  // J: down, then the hook round to the left.
  g('J', 0.56, path([[0.48, 1], [0.48, 0.26]], arc(0.26, 0.26, 0.22, 0.26, 0, -160))),
  // K: the stem, then in from the top right to the middle and out to the bottom right.
  g('K', 0.66, [[0.04, 1], [0.04, 0]], [[0.62, 1], [0.06, 0.46], [0.64, 0]]),
  // L: down and along.
  g('L', 0.56, [[0.04, 1], [0.04, 0], [0.54, 0]]),
  // M: the left stem, the V from the top, the right stem.
  g('M', 0.86, [[0.04, 1], [0.04, 0]], [[0.04, 1], [0.43, 0.3], [0.82, 1]], [[0.82, 1], [0.82, 0]]),
  // N: the left stem, then from the top down the slant and up the right side.
  g('N', 0.72, [[0.04, 1], [0.04, 0]], [[0.04, 1], [0.68, 0], [0.68, 1]]),
  // O: anticlockwise all the way round from the top.
  g('O', 0.86, arc(0.43, 0.5, 0.41, 0.5, 90, 450)),
  // P: the stem, then the bow from the top to the middle.
  g('P', 0.62, [[0.04, 1], [0.04, 0]], path([[0.04, 1], [0.34, 1]], arc(0.34, 0.75, 0.24, 0.25, 90, -90), [[0.04, 0.5]])),
  // Q: the O, then the tail.
  g('Q', 0.88, arc(0.43, 0.5, 0.41, 0.5, 90, 450), [[0.5, 0.24], [0.84, -0.04]]),
  // R: the stem, then the bow from the top straight on into the leg.
  g('R', 0.66, [[0.04, 1], [0.04, 0]], path([[0.04, 1], [0.34, 1]], arc(0.34, 0.75, 0.24, 0.25, 90, -90), [[0.62, 0]])),
  // S: from the top right anticlockwise over the top, down through the middle, clockwise to the bottom left.
  g('S', 0.62, path(arc(0.31, 0.75, 0.27, 0.25, 30, 270), arc(0.31, 0.25, 0.29, 0.25, 90, -150))),
  // T: the roof left to right, then the stem from its middle.
  g('T', 0.7, [[0.02, 1], [0.68, 1]], [[0.35, 1], [0.35, 0]]),
  // U: down, round the bottom, up.
  g('U', 0.7, path([[0.04, 1], [0.04, 0.32]], arc(0.35, 0.32, 0.31, 0.32, 180, 360), [[0.66, 1]])),
  // V: down and up.
  g('V', 0.76, [[0.02, 1], [0.38, 0], [0.74, 1]]),
  // W: down, up, down, up.
  g('W', 1.06, [[0.02, 1], [0.27, 0], [0.53, 0.7], [0.79, 0], [1.04, 1]]),
  // X: top left to bottom right, then top right to bottom left.
  g('X', 0.7, [[0.03, 1], [0.67, 0]], [[0.67, 1], [0.03, 0]]),
  // Y: top left to the middle, then top right through the middle to the foot.
  g('Y', 0.72, [[0.03, 1], [0.36, 0.5]], [[0.69, 1], [0.36, 0.5], [0.36, 0]]),
  // Z: along, the slant down, along.
  g('Z', 0.66, [[0.04, 1], [0.62, 1], [0.04, 0], [0.62, 0]]),
]

export const GERMAN_PRINT_CAPITALS: GlyphSet = {
  id: 'de-druck-caps',
  name: 'Druckschrift (Großbuchstaben)',
  glyphs: Object.fromEntries(GERMAN_CAPITALS.map((glyph) => [glyph.char, glyph])),
}

export const ALPHABET = GERMAN_CAPITALS.map((glyph) => glyph.char)

/** Total length of a glyph's strokes in cap heights. */
export function glyphLength(glyph: Glyph): number {
  let total = 0
  for (const s of glyph.strokes) total += polylineLength(s)
  return total
}

export function polylineLength(points: readonly Pt[]): number {
  let total = 0
  for (let i = 1; i < points.length; i++) total += Math.hypot(points[i][0] - points[i - 1][0], points[i][1] - points[i - 1][1])
  return total
}
